import React, { memo, useEffect, Dispatch } from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import classNames from 'classnames';
import { Action } from 'redux';
import { connect } from 'react-redux';

import { selectors } from '@amplement/backend-connector';
import { getCompanyTabsSelector } from 'selectors/company';
import { getCurrentUserIdSelector } from 'selectors/user';

import { ItemTabDbModel, ItemTab, ItemTabEnriched } from 'components/NavigationBar/types';
import MenuIconButton from 'components/LeftMenu/MenuIconButton';

import { hashParameters } from 'const/url';
import { NOTIFICATION_CATEGORIES } from 'const/notification';

import { openDrawer } from 'actions/drawer';
import * as drawerTypes from 'types/drawer';

import { hasHashParameter, setHashParameter, getUrl } from 'utils/url';

type Props = {
    toggleSipDrawer: () => void;
    callNotificationCount?: number;
    currentPath: string;
    _user?: string | null;
    tabs?: ItemTabDbModel[]
} & WrappedComponentProps;

const Tabs = ({
    intl,
    toggleSipDrawer,
    callNotificationCount = 0,
    currentPath,
    _user,
    tabs = []
}: Props) => {
    const items: ItemTab[] = [
        {
            iconName: 'Phone',
            label: intl.formatMessage({ id: 'menuLeft.navigation.dashboard' }),
            url: getUrl('dashboard'),
            notificationCount: callNotificationCount,
            name: 'dashboard'
        },
        {
            iconName: 'Users',
            label: intl.formatMessage({ id: 'menuLeft.navigation.contacts' }),
            url: getUrl('internalContacts'),
            name: 'contacts'
        },
        {
            iconName: 'Search',
            label: intl.formatMessage({ id: 'menuLeft.navigation.search' }),
            name: 'search',
            url: getUrl('search'),
        }, {
            iconName: 'Calendar',
            label: intl.formatMessage({ id: 'menuLeft.navigation.scheduler' }),
            name: 'scheduler',
            url: getUrl('scheduler'),
        }
    ];

    useEffect(() => {
        if (hasHashParameter(hashParameters.CALL))
            toggleSipDrawer();
    }, []);

    const enrichedItems = tabs
        // https://github.com/amplement/support/issues/345
        ?.filter((tab: ItemTabDbModel) => !['phone', 'selfcare'].includes(tab.name))
        .map((tab: ItemTabDbModel): ItemTabEnriched => ({
            iconName: tab.iconName || 'PlusCircle',
            iconData: tab.iconData,
            label: tab.label || intl.formatMessage({ id: `menuLeft.navigation.${tab.name}` }),
            url: tab.url && (tab.target === '_blank' || tab.url?.startsWith('#')) ? tab.url : getUrl(`custom-${tab.name}`),
            iconBgColor: tab.iconBgColor || '',
            name: tab.name,
            target: tab.target === '_blank' ? (tab.url || tab.name) : undefined,
            ...(items.find(t => t.name === tab.name || t.name === tab.label) || {})
        }));

    return (
        enrichedItems && enrichedItems.map((link: ItemTabEnriched, i: number) => (
            <MenuIconButton
                key={`${link.label || link.url}-${i}`} // eslint-disable-line
                iconName={link.iconName}
                iconData={link.iconData}
                tooltipLabel={link.label}
                href={link.url && _user ? link.url.replace(':_user', _user) : undefined}
                id={link.name}
                onClick={link.onClick}
                className={classNames({
                    active: link.url && link.url === currentPath
                })}
                target={link.target}
                iconBgColor={link.iconBgColor}
                notificationCount={link.notificationCount}
            />
        ))
    );
}

const Component = memo(injectIntl(Tabs));

const mapStateToProps = (state) => ({
    _user: getCurrentUserIdSelector(state),
    tabs: getCompanyTabsSelector(state),
    callNotificationCount:
        (selectors.notifications.getUnreadNotificationCountByCategorySelector(state, NOTIFICATION_CATEGORIES.ROOM) || 0)
        + (selectors.voiceMails.unreadVoiceMailCountSelector(state) || 0),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
    toggleSipDrawer: () : void => {
        setHashParameter(hashParameters.CALL);
        dispatch(openDrawer(drawerTypes.DRAWER_SIP));
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(Component);
